<template>
    <div class="missed-call-handler-popover">
        <div class="popover-content">
            <!-- Status Badge -->
            <div class="status-section mb-3">
                <el-tag 
                    :type="is_enabled ? 'success' : 'info'"
                    size="medium"
                    class="status-badge"
                >
                    {{ is_enabled ? 'Active' : 'Disabled' }}
                </el-tag>
            </div>

            <!-- Description -->
            <div class="description-section mb-3" :class="{'mb-0': !can_manage_computed}">
                <p class="description-text">
                    AI Call Rescue automatically answers missed inbound calls using a system-managed AI agent. It greets callers, captures intent and contact info, and logs notes so your business never misses a call.
                </p>
            </div>

            <!-- Toggle Switch -->
            <div v-if="can_manage_computed" class="toggle-section mb-3">
                <div class="d-flex align-items-center justify-content-between">
                    <div>
                        <label class="toggle-label">Enable AI Call Rescue</label>
                        <p class="price-info-text">
                            AloAi Agents are billed at <strong class="price-text">${{ get_missed_call_handler_basic_tier_price() }}/min</strong> for basic voice models.
                        </p>
                    </div>
                    <el-switch
                        v-model="local_enabled"
                        active-color="#00a344"
                        @change="handle_missed_call_handler_toggle"
                        :disabled="loading"
                        size="medium"
                    />
                </div>
            </div>

            <!-- Link to Settings -->
            <div v-if="can_manage_computed" class="settings-link-section">
                <el-button
                    type="text"
                    @click="go_to_missed_call_handler_settings"
                    class="settings-link-button"
                >
                    <i class="fa fa-cog mr-1"></i>
                    Go to Calling Settings
                </el-button>
            </div>
        </div>
    </div>
</template>

<script>
import axios from 'axios';
import { mapActions, mapState } from 'vuex';
import auth from '../../auth';
import * as Consent from '../../constants/consent';
import * as Roles from '../../constants/roles';
import { acl_mixin } from '../../mixins';

export default {
    name: 'MissedCallHandlerPopover',
    
    mixins: [acl_mixin],
    
    props: {
        enabled: {
            type: Boolean,
            default: false
        }
    },
    
    data() {
        return {
            auth: auth,
            loading: false,
            local_enabled: this.enabled
        }
    },
    
    computed: {
        ...mapState('cache', ['current_company']),
        
        is_enabled() {
            return this.local_enabled
        },
        
        can_manage_computed() {
            return !!this.auth?.user?.profile && this.hasRole([Roles.COMPANY_ADMIN, Roles.BILLING_ADMIN])
        }
    },
    
    
    watch: {
        enabled(newVal) {
            if (this.local_enabled !== newVal) {
                this.local_enabled = newVal
            }
        },
    },
    
    methods: {
        ...mapActions('cache', ['setCurrentCompany']),
        
        async update_missed_call_handler_company_details() {
            const response = await axios.get(`/api/v1/company/${this.current_company.id}`)
            const updatedCompany = response.data
            this.setCurrentCompany(updatedCompany)
            return updatedCompany
        },
        
        get_missed_call_handler_basic_tier_price() {
            const company = this.current_company
            const rate = company?.rate
            
            if (rate?.aloai_voice_bot_basic_minute) {
                return parseFloat(rate.aloai_voice_bot_basic_minute).toFixed(2)
            }
            
            return '0.10'
        },
        
        async check_missed_call_handler_consent() {
            try {
                const response = await axios.get(`/api/v1/consent/${Consent.CONSENT_TYPE_MISSED_CALL_HANDLER}`)
                return response.data
            } catch (error) {
                console.error('Failed to check consent:', error)
                return { has_consent: false, show_modal: false, consent_decision: null }
            }
        },
        
        async handle_missed_call_handler_toggle(value) {
            if (value) {
                const consentData = await this.check_missed_call_handler_consent()
                const hasAcceptedConsent = consentData.has_consent && consentData.consent_decision === 'accepted'
                
                if (consentData.show_modal || (consentData.has_consent && !hasAcceptedConsent)) {
                    this.local_enabled = this.enabled
                    this.$emit('consent-required', value)
                    return
                }
            }
            
            this.loading = true
            
            try {
                await axios.patch('/api/v1/company/flag', {
                    flag_name: 'aloai_missed_call_handler_enabled',
                    enabled: value
                })
                
                const updatedCompany = await this.update_missed_call_handler_company_details()
                
                this.local_enabled = updatedCompany.aloai_missed_call_handler_enabled || value
                
                this.$notify({
                    title: 'Settings',
                    message: value ? 'AI Call Rescue enabled for your account.' : 'AI Call Rescue remains off.',
                    type: 'success'
                })
                
                this.$emit('status-changed', value)
            } catch (error) {
                this.local_enabled = this.enabled
                this.$root.handleErrors(error.response)
            } finally {
                this.loading = false
            }
        },
        
        go_to_missed_call_handler_settings() {
            this.$router.push({ 
                name: 'Account', 
                query: { tab: 'calling-settings' },
                hash: '#aloai_missed_call_handler'
            })
            this.$emit('close')
        }
    }
}
</script>

<style scoped>
.missed-call-handler-popover {
    min-width: 320px;
    max-width: 400px;
}

.popover-content {
    padding: 16px;
}

.status-section {
    display: flex;
    align-items: center;
}

.status-badge {
    font-weight: 600;
    font-size: 13px;
}

.description-section {
    border-top: 1px solid #e4e7ed;
    border-bottom: 1px solid #e4e7ed;
    padding: 12px 0;
}

.description-text {
    margin: 0;
    font-size: 13px;
    line-height: 1.6;
    color: #606266;
}

.toggle-section {
    padding: 8px 0;
}

.toggle-label {
    margin: 0;
    font-size: 14px;
    font-weight: 500;
    color: #303133;
}

.price-info-text {
    margin: 4px 0 0 0;
    font-size: 12px;
    line-height: 1.4;
    color: #909399;
}

.price-text {
    font-size: 12px !important;
    font-weight: 600;
    color: #303133;
}

.settings-link-section {
    border-top: 1px solid #e4e7ed;
    padding-top: 12px;
}

.settings-link-button {
    width: 100%;
    text-align: left;
    padding: 8px 0;
    color: #409EFF;
    font-size: 13px;
}

.settings-link-button:hover {
    color: #66b1ff;
}
</style>

